import { Fragment } from 'react'
import { PlusCircle, Trash2 } from 'lucide-react'
import {
  AdditionOption,
  ExceptionOption,
  ProductAdditionsBlock,
  ProductExceptionsBlock,
} from '../../types/product'
import { inputClass, labelClass, surfaceClass, cn } from '../../utils/theme'

interface ProductOptionsState {
  additions: ProductAdditionsBlock
  exceptions: ProductExceptionsBlock
}

interface ProductOptionsEditorProps {
  value?: ProductOptionsState
  onChange: (value: ProductOptionsState) => void
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^\p{Letter}\p{Number}\s-]/gu, '')
    .trim()
    .replace(/\s+/g, '-')
}

function createEmptyAddition(): AdditionOption {
  return {
    key: '',
    name: '',
    priceDelta: 0,
    maxQty: 1,
    active: true,
  }
}

function createEmptyException(): ExceptionOption {
  return {
    key: '',
    name: '',
    affectsPrice: false,
    priceDelta: 0,
    active: true,
  }
}

function normalizeState(state?: ProductOptionsState): ProductOptionsState {
  return {
    additions: state?.additions
      ? {
          enabled: Boolean(state.additions.enabled),
          options: (state.additions.options ?? []).map((option) => ({
            ...option,
            priceDelta: Number(option.priceDelta ?? 0),
            maxQty: Math.max(1, Number(option.maxQty ?? 1)),
            active: Boolean(option.active),
          })),
        }
      : { enabled: false, options: [] },
    exceptions: state?.exceptions
      ? {
          enabled: Boolean(state.exceptions.enabled),
          options: (state.exceptions.options ?? []).map((option) => ({
            ...option,
            affectsPrice: Boolean(option.affectsPrice),
            priceDelta: option.affectsPrice ? Number(option.priceDelta ?? 0) : 0,
            active: Boolean(option.active),
          })),
        }
      : { enabled: false, options: [] },
  }
}

export default function ProductOptionsEditor({ value, onChange }: ProductOptionsEditorProps) {
  const state = normalizeState(value)

  const updateState = (next: ProductOptionsState) => {
    onChange(normalizeState(next))
  }

  const handleToggleAdditions = (enabled: boolean) => {
    updateState({
      additions: enabled
        ? state.additions.enabled
          ? state.additions
          : {
              enabled: true,
              options:
                state.additions.options.length > 0
                  ? state.additions.options
                  : [createEmptyAddition()],
            }
        : { enabled: false, options: state.additions.options },
      exceptions: state.exceptions,
    })
  }

  const handleToggleExceptions = (enabled: boolean) => {
    updateState({
      additions: state.additions,
      exceptions: enabled
        ? state.exceptions.enabled
          ? state.exceptions
          : {
              enabled: true,
              options:
                state.exceptions.options.length > 0
                  ? state.exceptions.options
                  : [createEmptyException()],
            }
        : { enabled: false, options: state.exceptions.options },
    })
  }

  const handleAdditionChange = <Key extends keyof AdditionOption>(
    index: number,
    key: Key,
    value: AdditionOption[Key],
  ) => {
    const options = state.additions.options.map((option, optionIndex) => {
      if (optionIndex !== index) return option

      const nextOption: AdditionOption = { ...option }
      if (key === 'name') {
        const previousSlug = slugify(option.name)
        const nextSlug = slugify(String(value))
        nextOption.name = String(value)
        if (!option.key || option.key === previousSlug) {
          nextOption.key = nextSlug
        }
        return nextOption
      }

      if (key === 'key') {
        nextOption.key = String(value)
        return nextOption
      }

      if (key === 'priceDelta') {
        nextOption.priceDelta = Number(value ?? 0)
        return nextOption
      }

      if (key === 'maxQty') {
        const numeric = Number(value ?? 1)
        nextOption.maxQty = Number.isNaN(numeric) || numeric < 1 ? 1 : Math.trunc(numeric)
        return nextOption
      }

      if (key === 'active') {
        nextOption.active = Boolean(value)
        return nextOption
      }

      return { ...option, [key]: value }
    })

    updateState({
      additions: { ...state.additions, options },
      exceptions: state.exceptions,
    })
  }

  const handleExceptionChange = <Key extends keyof ExceptionOption>(
    index: number,
    key: Key,
    value: ExceptionOption[Key],
  ) => {
    const options = state.exceptions.options.map((option, optionIndex) => {
      if (optionIndex !== index) return option

      const nextOption: ExceptionOption = { ...option }
      if (key === 'name') {
        const previousSlug = slugify(option.name)
        const nextSlug = slugify(String(value))
        nextOption.name = String(value)
        if (!option.key || option.key === previousSlug) {
          nextOption.key = nextSlug
        }
        return nextOption
      }

      if (key === 'key') {
        nextOption.key = String(value)
        return nextOption
      }

      if (key === 'affectsPrice') {
        const affectsPrice = Boolean(value)
        nextOption.affectsPrice = affectsPrice
        if (!affectsPrice) {
          nextOption.priceDelta = 0
        }
        return nextOption
      }

      if (key === 'priceDelta') {
        nextOption.priceDelta = Number(value ?? 0)
        return nextOption
      }

      if (key === 'active') {
        nextOption.active = Boolean(value)
        return nextOption
      }

      return { ...option, [key]: value }
    })

    updateState({
      additions: state.additions,
      exceptions: { ...state.exceptions, options },
    })
  }

  const handleAddAddition = () => {
    updateState({
      additions: {
        enabled: true,
        options: [...state.additions.options, createEmptyAddition()],
      },
      exceptions: state.exceptions,
    })
  }

  const handleRemoveAddition = (index: number) => {
    const options = state.additions.options.filter((_, optionIndex) => optionIndex !== index)
    updateState({
      additions: { enabled: options.length > 0, options },
      exceptions: state.exceptions,
    })
  }

  const handleAddException = () => {
    updateState({
      additions: state.additions,
      exceptions: {
        enabled: true,
        options: [...state.exceptions.options, createEmptyException()],
      },
    })
  }

  const handleRemoveException = (index: number) => {
    const options = state.exceptions.options.filter((_, optionIndex) => optionIndex !== index)
    updateState({
      additions: state.additions,
      exceptions: { enabled: options.length > 0, options },
    })
  }

  return (
    <div className="space-y-6">
      <section className={cn(surfaceClass(), 'overflow-hidden rounded-xl')}>
        <header className="flex flex-col gap-2 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Adiciones</h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Configura extras opcionales y su precio adicional.
            </p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
            <input
              type="checkbox"
              checked={state.additions.enabled}
              onChange={(event) => handleToggleAdditions(event.target.checked)}
              aria-label="Habilitar adiciones"
            />
            <span>{state.additions.enabled ? 'Habilitadas' : 'Deshabilitadas'}</span>
          </label>
        </header>
        {state.additions.enabled ? (
          <div className="space-y-4 p-4">
            {state.additions.options.map((option, index) => {
              const nameId = `addition-name-${index}`
              const keyId = `addition-key-${index}`
              const priceId = `addition-price-${index}`
              const qtyId = `addition-qty-${index}`
              const activeId = `addition-active-${index}`
              return (
                <Fragment key={index}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className={labelClass()} htmlFor={nameId}>
                        Nombre de la adición {index + 1}
                      </label>
                      <input
                        id={nameId}
                        type="text"
                        value={option.name}
                        onChange={(event) => handleAdditionChange(index, 'name', event.target.value)}
                        className={inputClass()}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className={labelClass()} htmlFor={keyId}>
                        Identificador {index + 1}
                      </label>
                      <input
                        id={keyId}
                        type="text"
                        value={option.key}
                        onChange={(event) => handleAdditionChange(index, 'key', event.target.value)}
                        className={inputClass()}
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <label className={labelClass()} htmlFor={priceId}>
                        Precio adicional {index + 1}
                      </label>
                      <input
                        id={priceId}
                        type="number"
                        min="0"
                        step="0.01"
                        value={option.priceDelta}
                        onChange={(event) => handleAdditionChange(index, 'priceDelta', Number(event.target.value))}
                        className={inputClass()}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className={labelClass()} htmlFor={qtyId}>
                        Cantidad máxima {index + 1}
                      </label>
                      <input
                        id={qtyId}
                        type="number"
                        min="1"
                        step="1"
                        value={option.maxQty}
                        onChange={(event) => handleAdditionChange(index, 'maxQty', Number(event.target.value))}
                        className={inputClass()}
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        id={activeId}
                        type="checkbox"
                        checked={option.active}
                        onChange={(event) => handleAdditionChange(index, 'active', event.target.checked)}
                        aria-label={`Adición activa ${index + 1}`}
                      />
                      <label htmlFor={activeId} className="text-sm text-neutral-600 dark:text-neutral-300">
                        Activa
                      </label>
                      <button
                        type="button"
                        className={cn(
                          'ml-auto inline-flex items-center rounded-full border border-transparent bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600 transition-colors',
                          'hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20 dark:focus-visible:ring-offset-neutral-900',
                        )}
                        onClick={() => handleRemoveAddition(index)}
                        aria-label={`Eliminar adición ${index + 1}`}
                      >
                        <Trash2 className="mr-1 h-4 w-4" /> Eliminar
                      </button>
                    </div>
                  </div>
                </Fragment>
              )
            })}
            <button
              type="button"
              className={cn(
                'inline-flex items-center rounded-full border border-dashed border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-600 transition-colors',
                'hover:border-blue-500 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-blue-400 dark:hover:text-blue-300 dark:focus-visible:ring-offset-neutral-900',
              )}
              onClick={handleAddAddition}
            >
              <PlusCircle className="mr-2 h-4 w-4" /> Agregar adición
            </button>
          </div>
        ) : null}
      </section>

      <section className={cn(surfaceClass(), 'overflow-hidden rounded-xl')}>
        <header className="flex flex-col gap-2 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Excepciones</h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Define ingredientes que el cliente puede retirar o ajustar.
            </p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
            <input
              type="checkbox"
              checked={state.exceptions.enabled}
              onChange={(event) => handleToggleExceptions(event.target.checked)}
              aria-label="Habilitar excepciones"
            />
            <span>{state.exceptions.enabled ? 'Habilitadas' : 'Deshabilitadas'}</span>
          </label>
        </header>
        {state.exceptions.enabled ? (
          <div className="space-y-4 p-4">
            {state.exceptions.options.map((option, index) => {
              const nameId = `exception-name-${index}`
              const keyId = `exception-key-${index}`
              const affectsId = `exception-affects-${index}`
              const priceId = `exception-price-${index}`
              const activeId = `exception-active-${index}`
              return (
                <Fragment key={index}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className={labelClass()} htmlFor={nameId}>
                        Nombre de la excepción {index + 1}
                      </label>
                      <input
                        id={nameId}
                        type="text"
                        value={option.name}
                        onChange={(event) => handleExceptionChange(index, 'name', event.target.value)}
                        className={inputClass()}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className={labelClass()} htmlFor={keyId}>
                        Identificador {index + 1}
                      </label>
                      <input
                        id={keyId}
                        type="text"
                        value={option.key}
                        onChange={(event) => handleExceptionChange(index, 'key', event.target.value)}
                        className={inputClass()}
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        id={affectsId}
                        type="checkbox"
                        checked={Boolean(option.affectsPrice)}
                        onChange={(event) => handleExceptionChange(index, 'affectsPrice', event.target.checked)}
                        aria-label={`La excepción afecta el precio ${index + 1}`}
                      />
                      <label htmlFor={affectsId} className="text-sm text-neutral-600 dark:text-neutral-300">
                        Afecta el precio
                      </label>
                    </div>
                    <div className="space-y-2">
                      <label className={labelClass()} htmlFor={priceId}>
                        Variación de precio {index + 1}
                      </label>
                      <input
                        id={priceId}
                        type="number"
                        min="0"
                        step="0.01"
                        value={option.affectsPrice ? option.priceDelta ?? 0 : 0}
                        onChange={(event) => handleExceptionChange(index, 'priceDelta', Number(event.target.value))}
                        disabled={!option.affectsPrice}
                        className={cn(
                          inputClass(),
                          !option.affectsPrice && 'cursor-not-allowed opacity-60',
                        )}
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        id={activeId}
                        type="checkbox"
                        checked={option.active}
                        onChange={(event) => handleExceptionChange(index, 'active', event.target.checked)}
                        aria-label={`Excepción activa ${index + 1}`}
                      />
                      <label htmlFor={activeId} className="text-sm text-neutral-600 dark:text-neutral-300">
                        Activa
                      </label>
                      <button
                        type="button"
                        className={cn(
                          'ml-auto inline-flex items-center rounded-full border border-transparent bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600 transition-colors',
                          'hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20 dark:focus-visible:ring-offset-neutral-900',
                        )}
                        onClick={() => handleRemoveException(index)}
                        aria-label={`Eliminar excepción ${index + 1}`}
                      >
                        <Trash2 className="mr-1 h-4 w-4" /> Eliminar
                      </button>
                    </div>
                  </div>
                </Fragment>
              )
            })}
            <button
              type="button"
              className={cn(
                'inline-flex items-center rounded-full border border-dashed border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-600 transition-colors',
                'hover:border-blue-500 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-blue-400 dark:hover:text-blue-300 dark:focus-visible:ring-offset-neutral-900',
              )}
              onClick={handleAddException}
            >
              <PlusCircle className="mr-2 h-4 w-4" /> Agregar excepción
            </button>
          </div>
        ) : null}
      </section>
    </div>
  )
}
